import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Award, Handshake, Megaphone, Crown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { teamMembers, values } from '../data/mock';

const iconMap = {
  Crown: Crown,
  Users: Users,
  Handshake: Handshake,
  Megaphone: Megaphone,
  Award: Award,
};

// Rotating colors from the brand
const valueColors = [
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
  { bg: 'from-brand-green to-green-600', shadow: 'shadow-green-500/20' },
  { bg: 'from-brand-red to-red-600', shadow: 'shadow-red-500/20' },
  { bg: 'from-brand-navy to-navy-700', shadow: 'shadow-navy-500/20' },
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
];

export default function AboutPage() {
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-64 h-64 bg-green-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">About Us</span>
            </nav>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Empowering Men of Color in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Early Childhood Education
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              DC Men in ECE is dedicated to transforming the early childhood education landscape by supporting, developing, and advancing men of color in the field.
            </p>
          </div>
        </div>
      </section>

      {/* How We Got Here */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
                <Target className="w-4 h-4 text-brand-gold" />
                <span className="text-sm font-medium text-gold-700">Our Journey</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">How We Got Here</h2>
              <div className="space-y-4 text-navy-600 leading-relaxed">
                <p>
                  DC Men in Early Childhood Education (ECE) was launched in April 2022 during the national observance of the "Month of the Young Child." Initiated by the DC Early Learning Collaborative, a trailblazer in early childhood advocacy, our mission is to empower men of color in the ECE field.
                </p>
                <p>
                  From a modest networking reception attended by 40 men, we have grown rapidly, hosting our first annual city-wide conference in October 2023 with over 200 participants.
                </p>
                <p>
                  Our commitment to recruitment, retention, and advancement is driving a more diverse and inclusive early childhood education landscape in Washington, D.C.
                </p>
              </div>
            </div>
            
            {/* Timeline Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gold-50 to-green-50 rounded-3xl p-4 shadow-xl overflow-hidden">
                <img 
                  src="/images/about-photo.jpg" 
                  alt="DC Men in ECE Event"
                  className="w-full h-80 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Need */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-navy-800/50 border border-navy-700/50 rounded-2xl p-6 text-center">
                  <div className="text-5xl font-bold text-brand-gold mb-2">3.3%</div>
                  <p className="text-navy-300 text-sm">Men in ECE workforce</p>
                </div>
                <div className="bg-navy-800/50 border border-navy-700/50 rounded-2xl p-6 text-center">
                  <div className="text-5xl font-bold text-brand-red mb-2">&lt;1%</div>
                  <p className="text-navy-300 text-sm">Men of Color</p>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">The Need for Such an Organization</h2>
              <div className="space-y-4 text-navy-200 leading-relaxed">
                <p>
                  In early childhood education, men make up just 3.3% of the workforce, with men of color representing less than 1%. This lack of representation means many children miss out on having role models who look like them and reflect their communities.
                </p>
                <p>
                  This low number of male educators also leads to feelings of isolation, resulting in lower retention and advancement rates. Our efforts aim to create a supportive community, ensuring educators not only enter the field but thrive in it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">What Makes Us Different</h2>
            <p className="text-navy-600">Our unique approach combines professional development with community building to create lasting change.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.slice(0, 5).map((value, index) => {
              const Icon = iconMap[value.icon] || Award;
              const colors = valueColors[index];
              return (
                <Card key={index} className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg ${colors.shadow}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3">{value.title}</h3>
                    <p className="text-navy-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-to-br from-navy-50 to-gold-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">The Faces Behind the Mission</h2>
            <p className="text-navy-600">Meet the dedicated leaders driving DC Men in ECE forward.</p>
          </div>

          {/* Co-Conveners Row */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {teamMembers.filter(member => member.role === 'Co-Convener').map((member, index) => (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden bg-white cursor-pointer"
                onClick={() => setSelectedMember(member)}
              >
                <CardContent className="p-6">
                  {/* Avatar */}
                  <div className="w-28 h-28 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-300">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gold-100 to-green-50 flex items-center justify-center">
                        <Users className="w-10 h-10 text-brand-green" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-navy-900 mb-1">{member.name}</h3>
                    <p className="text-brand-green font-medium text-sm mb-4">{member.role}</p>
                    <p className="text-navy-600 text-sm leading-relaxed line-clamp-3">{member.bio}</p>
                    <p className="text-brand-green text-sm font-medium mt-3 group-hover:underline">Read full bio →</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Senior Advisor Emeritus - Centered Below */}
          <div className="flex justify-center">
            {teamMembers.filter(member => member.role === 'Senior Advisor Emeritus').map((member, index) => (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden bg-white max-w-sm cursor-pointer"
                onClick={() => setSelectedMember(member)}
              >
                <CardContent className="p-6">
                  {/* Avatar */}
                  <div className="w-28 h-28 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-300">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gold-100 to-green-50 flex items-center justify-center">
                        <Users className="w-10 h-10 text-brand-green" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-navy-900 mb-1">{member.name}</h3>
                    <p className="text-brand-gold font-medium text-sm mb-4">{member.role}</p>
                    <p className="text-navy-600 text-sm leading-relaxed line-clamp-3">{member.bio}</p>
                    <p className="text-brand-gold text-sm font-medium mt-3 group-hover:underline">Read full bio →</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Bio Modal */}
        <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedMember && (
              <>
                <DialogHeader>
                  <div className="flex flex-col sm:flex-row items-center gap-6 mb-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-navy-100 shadow-lg flex-shrink-0">
                      {selectedMember.image ? (
                        <img 
                          src={selectedMember.image} 
                          alt={selectedMember.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gold-100 to-green-50 flex items-center justify-center">
                          <Users className="w-10 h-10 text-brand-green" />
                        </div>
                      )}
                    </div>
                    <div className="text-center sm:text-left">
                      <DialogTitle className="text-2xl font-bold text-navy-900">{selectedMember.name}</DialogTitle>
                      <p className={`font-medium mt-1 ${selectedMember.role === 'Senior Advisor Emeritus' ? 'text-brand-gold' : 'text-brand-green'}`}>
                        {selectedMember.role}
                      </p>
                    </div>
                  </div>
                </DialogHeader>
                <div className="space-y-4 text-navy-600 leading-relaxed">
                  <p>{selectedMember.bio}</p>
                  {selectedMember.fullBio && (
                    <p>{selectedMember.fullBio}</p>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Be Part of the Change
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Join us in transforming early childhood education in Washington, D.C. and beyond.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/membership">
              <Button size="lg" className="bg-brand-green hover:bg-green-600 text-white font-semibold px-8 shadow-xl shadow-green-500/20">
                Become a Member
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
